import isSSR from '@shared/utils/isSSR';
import { ActivitySession } from './ActivitySession';
import { addEventListeners } from './utils/addEventListener';
import { DOM_EVENT } from './utils/constants';
import { timestamp } from './utils/Time';

export type PersistStore = {
  load: () => string | null | undefined;
  save: (sessionId: string, expireTimestamp: number) => void;
  clear: () => void;
};

export interface ActivitySessionManagerConfig {
  maxAgeSec?: number;
  sessionExpirationDelaySec?: number;
  onActivitySessionFinish?: (activitySession: ActivitySession) => void;
  onActivitySessionStart?: (activitySession: ActivitySession) => void;
  canRegenerate?: (activitySession: ActivitySession) => boolean;
  persistStore?: PersistStore;
}

const TIME_10_MIN_SEC = 10 * 60;

export class ActivitySessionManager {
  currentActivitySession: ActivitySession;
  maxAgeSec: number;
  sessionExpirationDelaySec: number;
  timerId?: ReturnType<typeof setTimeout>;
  onActivitySessionFinish?: (activitySession: ActivitySession) => void;
  onActivitySessionStart?: (activitySession: ActivitySession) => void;
  canRegenerate?: (activitySession: ActivitySession) => boolean;
  stopEventListeners: () => void;
  persistInitActivitySessionId?: string | null = null;
  persistStore?: PersistStore;

  constructor({
    maxAgeSec = 1,
    sessionExpirationDelaySec = TIME_10_MIN_SEC,
    onActivitySessionFinish,
    onActivitySessionStart,
    canRegenerate,
    persistStore,
  }: ActivitySessionManagerConfig = {}) {
    this.persistStore = persistStore;
    const activitySessionId = persistStore?.load();
    this.currentActivitySession = new ActivitySession(activitySessionId ? { activitySessionId } : undefined);
    this.persistInitActivitySessionId = activitySessionId;

    this.maxAgeSec = maxAgeSec;
    this.sessionExpirationDelaySec = sessionExpirationDelaySec;
    this.onActivitySessionFinish = onActivitySessionFinish;
    this.onActivitySessionStart = onActivitySessionStart;
    this.canRegenerate = canRegenerate;

    // TODO: mock up test
    if (!isSSR) {
      const { stop } = addEventListeners(
        globalThis.window,
        [DOM_EVENT.CLICK, DOM_EVENT.TOUCH_START, DOM_EVENT.KEY_DOWN, DOM_EVENT.SCROLL],
        this.expand,
        { capture: true, passive: true },
      );
      this.stopEventListeners = stop;
    } else {
      this.stopEventListeners = () => {};
    }
  }

  regenerate = () => {
    this.timerId = setTimeout(() => {
      if (this.canRegenerate && !this.canRegenerate(this.currentActivitySession)) {
        this.regenerate();
        return;
      }

      if (this.currentActivitySession.signalingCount > 0) {
        this.currentActivitySession.end();
        this.onActivitySessionFinish?.(this.currentActivitySession);
      }

      this.persistStore?.clear();
      this.currentActivitySession = new ActivitySession();

      this.currentActivitySession.start();
      this.regenerate();
    }, this.maxAgeSec * 1000);
  };

  expand = () => {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }

    this?.persistStore?.save(
      this.currentActivitySession.activitySessionId,
      timestamp() + this.sessionExpirationDelaySec,
    );
    this.timerId = setTimeout(() => {
      this.regenerate();
    }, this.sessionExpirationDelaySec * 1000);
  };

  start() {
    this.currentActivitySession.start();
    if (this.persistInitActivitySessionId) {
      this.expand();
    } else {
      this.regenerate();
    }
  }

  signaling = () => {
    this.currentActivitySession.signal();

    if (this.currentActivitySession.signalingCount === 1) {
      if (this.persistInitActivitySessionId !== this.currentActivitySession.activitySessionId) {
        this.onActivitySessionStart?.(this.currentActivitySession);
      }
      this?.persistStore?.save(
        this.currentActivitySession.activitySessionId,
        timestamp() + this.sessionExpirationDelaySec,
      );
    }

    this.expand();
  };

  stop() {
    if (this.timerId) {
      clearTimeout(this.timerId);
    }
    this.stopEventListeners();
  }
}
